<script setup>
import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";

const { fetchPage, fetchMedias } = useFetchAPI();
const { setMeta } = useMeta();
const { data } = await fetchPage("location");

const locationData = computed(() => {
    const meta = data.value?.meta_box || {};
    const {
        title = [],
        locationInfo = [],
        parkingInfo = [],
        InterPhoto = [],
    } = meta;
    return { title, locationInfo, parkingInfo, InterPhoto };
});

const { data: images } = await fetchMedias(
    locationData.value.InterPhoto,
    "location",
);

const { title: pageTitle, locationInfo, parkingInfo } = locationData.value;

const updateGoogleMapUrl = (url) => {
    currentMapUrl.value = extractIframeSrc(url);
    scrollToSection("#map-section");
};

const extractIframeSrc = (iframe) => {
    const match = iframe.match(/<iframe[^>]+src="([^"]+)"/);
    return match ? match[1] : null;
};

const scrollToSection = (targetPosition) => {
    gsap.to(window, {
        duration: 1,
        scrollTo: {
            y: targetPosition,
            offsetY: 40,
        },
        ease: "power2.out",
    });
};

const showVisitDialogue = ref(false);
const currentMapUrl = ref(extractIframeSrc(locationInfo?.mapButton?.link));

// text amimation
const textRefs = ref([]);
const scrollTriggerEnabled = ref(false);

if (import.meta.client) {
    gsap.registerPlugin(ScrollToPlugin);
}

onMounted(() => {
    textRefs.value.forEach((textRef, index) => {
        useScrollTrigger(
            computed(() => textRef?.rootRef),
            () => {
                textRef?.animate?.({ delay: index * 0.5 });
            },
            {
                enabled: scrollTriggerEnabled,
            },
        );
    });

    setTimeout(() => {
        scrollTriggerEnabled.value = true;
    }, 2000);
});

await setMeta(data.value);
</script>

<template>
    <section
        class="slt-container overflow-x-hidden bg-base-bgGrey font-bold lg:pt-[119px]"
    >
        <CustomLocationHero :title="pageTitle" />

        <div class="content-indent pt-12 lg:pt-[100px] xl:pt-[120px]">
            <BaseDialogueVisitUs
                v-if="showVisitDialogue"
                @close="showVisitDialogue = false"
            />

            <!-- Content -->
            <section class="grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
                <BaseCard class="flex flex-col">
                    <BaseFormatText2
                        type="h3"
                        :ref="(el) => textRefs.push(el)"
                        :text="locationInfo?.title"
                        class="slt-h3 mb-6 whitespace-pre-wrap sm:mb-8 md:mb-9"
                    />
                    <BaseFormatText
                        type="h6"
                        :text="locationInfo?.address"
                        class="slt-h6 mb-3 whitespace-pre-wrap"
                    />

                    <!-- Phone & Fax -->
                    <div
                        class="slt-h5 mb-1 flex items-center gap-[2.03vw] xs:gap-2"
                    >
                        <SvgPhone
                            class="size-[5.08vw] p-[0.125rem] xs:size-5"
                        />
                        <span lang="en">{{ locationInfo?.tel }}</span>
                    </div>
                    <div
                        class="slt-h5 mb-1 flex items-center gap-[2.03vw] xs:gap-2"
                    >
                        <SvgFax class="size-[5.08vw] p-[0.125rem] xs:size-5" />
                        <span lang="en">{{ locationInfo?.fax }}</span>
                    </div>

                    <!-- Buttons -->
                    <div
                        class="mt-auto flex flex-col gap-2 pt-[20px] md:flex-row md:pt-6"
                    >
                        <BaseIconButton
                            variant="secondary"
                            :isLink="false"
                            class="w-full md:min-w-[240px] lg:min-w-[unset] lg:whitespace-nowrap"
                            :ariaLabel="$t('ariaLabel.location.map')"
                            @click="
                                updateGoogleMapUrl(
                                    locationInfo?.mapButton?.link,
                                )
                            "
                            v-sendGAsignal="{
                                event_category:
                                    locationInfo?.mapButton?.category,
                                event_label: locationInfo?.mapButton?.label,
                            }"
                        >
                            <template #svg><SvgLocation24 /></template>
                            {{ locationInfo?.mapButton?.text }}
                        </BaseIconButton>

                        <BaseIconButton
                            variant="primary"
                            :isLink="false"
                            class="w-full md:min-w-[130px] lg:min-w-[unset]"
                            :ariaLabel="$t('ariaLabel.location.visit')"
                            @click="showVisitDialogue = true"
                            v-sendGAsignal="{
                                event_category:
                                    locationInfo?.visitButton?.category,
                                event_label: locationInfo?.visitButton?.label,
                            }"
                        >
                            {{ locationInfo?.visitButton?.text }}
                        </BaseIconButton>
                    </div>
                </BaseCard>

                <BaseCard class="flex flex-col gap-6 md:order-3 md:gap-9">
                    <BaseFormatText2
                        type="h3"
                        :ref="(el) => textRefs.push(el)"
                        :text="parkingInfo?.title"
                        class="slt-h3 whitespace-pre-wrap sm:mb-[8px] md:mb-0"
                    />
                    <!-- Parking Info -->
                    <div
                        v-for="(item, index) in parkingInfo?.data"
                        :key="`parking-${index}`"
                        class="flex flex-col"
                    >
                        <BaseFormatText
                            type="h6"
                            :text="item?.name"
                            class="slt-h6 mb-1 whitespace-pre-wrap"
                        />
                        <BaseFormatText
                            type="h6"
                            :text="item?.address"
                            class="slt-h6 mb-6 whitespace-pre-wrap"
                        />

                        <!-- Buttons -->
                        <div class="mt-auto flex flex-col gap-2 md:flex-row">
                            <BaseIconButton
                                variant="secondary"
                                :isLink="false"
                                class="w-full md:min-w-[240px] lg:min-w-[unset] lg:whitespace-nowrap"
                                :ariaLabel="$t('ariaLabel.location.map')"
                                @click="
                                    updateGoogleMapUrl(item?.buttonText?.link)
                                "
                                v-sendGAsignal="{
                                    event_category: item?.buttonText?.category,
                                    event_label: item?.buttonText?.label,
                                }"
                            >
                                <template #svg><SvgLocation24 /></template>
                                {{ item?.buttonText?.text }}
                            </BaseIconButton>
                            <div
                                class="w-full md:min-w-[130px] lg:min-w-[unset]"
                            ></div>
                        </div>
                    </div>
                </BaseCard>

                <article
                    id="map-section"
                    class="flex h-[509px] w-full items-center justify-center overflow-hidden rounded-2xl sm:col-span-2 md:col-span-1 md:row-span-2 md:h-full"
                >
                    <iframe
                        :src="currentMapUrl"
                        class="h-full w-full rounded-2xl"
                        style="border: 0"
                        allowfullscreen=""
                        loading="lazy"
                        referrerpolicy="no-referrer-when-downgrade"
                    ></iframe>
                </article>
            </section>
        </div>

        <CustomLightbox :images="images" className="mb-[120px] mt-[40px]" />
    </section>
</template>

<style scoped>
.content-indent {
    @apply mx-auto w-[92.11vw] max-w-[var(--slt-container-width-sm)] sm:w-[89.71vw] lg:w-[71vw];
}

@media (min-width: 1024px) {
    :deep(._button) {
        height: 48px !important;
        p {
            font-size: 17px !important;
        }
    }
    :deep(._svg) {
        width: 24px !important;
        height: 24px !important;
    }
}
</style>
